import { FC, useCallback, useEffect, useState } from 'react';
import type { AdminTenantListItem } from '@libs/types';
import { Box, Button, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { CheckIcon, PencilSimpleIcon, XIcon } from '@phosphor-icons/react';

import { UpdateTenantDto, useUpdateTenant } from '../api/admin-api';

import { formatDate } from './adminFormat';
import { adminButtonSx, adminColors } from './adminTheme';

const SECTORS = [
  'Financial Services',
  'Banking',
  'Insurance',
  'Healthcare',
  'Education',
  'Government',
  'Technology',
  'Retail',
  'Transport & Logistics',
  'Energy & Utilities',
  'Telecom',
  'Other',
];

/**
 * Editable admin view of a tenant's Keycloak organization info. Opens read-only;
 * "Edit" reveals inline inputs with Save / Cancel. The slug (`name`) is
 * immutable in Keycloak (it appears in the `organization` token claim and the
 * derived domains) so it is shown but never editable.
 */
export const OrganizationDetailsTab: FC<{ tenant: AdminTenantListItem }> = observer(({ tenant }) => {
  const theme = useTheme();
  const c = adminColors(theme);
  const btn = adminButtonSx(theme);
  const { t } = useTrans();
  const update = useUpdateTenant(tenant.id);

  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [location, setLocation] = useState('');
  const [sector, setSector] = useState('');
  const [dpo, setDpo] = useState('');
  const [commercialRegistration, setCommercialRegistration] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const seed = useCallback(() => {
    setDisplayName(tenant.displayName || '');
    setContactEmail(tenant.contactEmail || '');
    setLocation(tenant.location || '');
    setSector(tenant.sector || '');
    setDpo(tenant.dpo || '');
    setCommercialRegistration(tenant.commercialRegistration || '');
    setSubmitted(false);
  }, [tenant]);

  // Re-seed whenever fresh tenant data arrives while not actively editing.
  useEffect(() => {
    if (!editing) seed();
  }, [seed, editing]);

  const displayNameError = submitted && !displayName.trim();

  const handleCancel = () => {
    seed();
    setEditing(false);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    if (!displayName.trim()) return;
    const body: UpdateTenantDto = {
      displayName: displayName.trim(),
      contactEmail: contactEmail.trim() || undefined,
      location: location.trim() || undefined,
      sector: sector || undefined,
      dpo: dpo.trim() || undefined,
      commercialRegistration: commercialRegistration.trim() || undefined,
    };
    update.mutate(body, { onSuccess: () => setEditing(false) });
  };

  const readItems: { label: string; value: string; mono?: boolean }[] = [
    { label: t('app.admin.detail.orgDetails.slug'), value: tenant.name, mono: true },
    { label: t('app.admin.detail.orgDetails.displayName'), value: tenant.displayName || '—' },
    { label: t('app.admin.detail.orgDetails.email'), value: tenant.contactEmail || '—' },
    { label: t('app.admin.detail.orgDetails.location'), value: tenant.location || '—' },
    { label: t('app.admin.detail.orgDetails.sector'), value: tenant.sector || '—' },
    { label: t('app.admin.detail.orgDetails.dpo'), value: tenant.dpo || '—' },
    { label: t('app.admin.detail.orgDetails.commercialReg'), value: tenant.commercialRegistration || '—', mono: true },
    { label: t('app.admin.detail.orgDetails.createdAt'), value: formatDate(tenant.createdAt) },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
        {editing ? (
          <>
            <Button onClick={handleCancel} startIcon={<XIcon size={15} />} sx={{ ...btn.neutral, px: 2, py: 0.875, borderRadius: 2.5, fontSize: 12.5 }}>
              {t('app.admin.detail.orgDetails.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={update.isPending}
              startIcon={<CheckIcon size={15} />}
              sx={{ ...btn.solid, px: 2, py: 0.875, borderRadius: 2.5, fontSize: 12.5 }}
            >
              {t('app.admin.detail.orgDetails.save')}
            </Button>
          </>
        ) : (
          <Button
            onClick={() => setEditing(true)}
            startIcon={<PencilSimpleIcon size={15} />}
            sx={{ ...btn.neutral, px: 2, py: 0.875, borderRadius: 2.5, fontSize: 12.5 }}
          >
            {t('app.admin.detail.orgDetails.edit')}
          </Button>
        )}
      </Box>

      {editing ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)' } }}>
          <TextField
            label={t('app.admin.detail.orgDetails.slug')}
            value={tenant.name}
            disabled
            helperText={t('app.admin.detail.orgDetails.slugHint')}
            size="small"
            fullWidth
          />
          <TextField
            label={t('app.admin.detail.orgDetails.displayName')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            error={displayNameError}
            helperText={displayNameError ? t('app.admin.detail.orgDetails.validation.required') : undefined}
            size="small"
            fullWidth
          />
          <TextField
            label={t('app.admin.detail.orgDetails.email')}
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={t('app.admin.detail.orgDetails.location')}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            select
            label={t('app.admin.detail.orgDetails.sector')}
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            size="small"
            fullWidth
          >
            {SECTORS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('app.admin.detail.orgDetails.dpo')}
            value={dpo}
            onChange={(e) => setDpo(e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={t('app.admin.detail.orgDetails.commercialReg')}
            value={commercialRegistration}
            onChange={(e) => setCommercialRegistration(e.target.value)}
            size="small"
            fullWidth
          />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)' } }}>
          {readItems.map((kv) => (
            <Box key={kv.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.75, border: `1px solid ${c.border}`, borderRadius: 2.75 }}>
              <Typography sx={{ fontSize: 11, color: c.faint }}>{kv.label}</Typography>
              <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: c.ink, fontFamily: kv.mono ? 'monospace' : 'inherit', wordBreak: 'break-word' }}>
                {kv.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
});
